<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'

interface Category {
  uuid: string
  name: string
}

interface Player {
  uuid: string
  first_name: string
  last_name: string
  categories?: Category[]
}

interface Coach {
  uuid: string
  first_name: string
  last_name: string
  categories?: Category[]
}

const props = defineProps<{
  categories: Category[]
  players: Player[]
  coaches: Coach[]
  loadingPlayers?: boolean
  loadingCoaches?: boolean
}>()

const { t } = useI18n()

const selectedPlayers = defineModel<string[]>('selectedPlayers', { default: () => [] })
const selectedCoaches = defineModel<string[]>('selectedCoaches', { default: () => [] })

const playersAccordionOpen = ref(false)
const coachesAccordionOpen = ref(false)
const playerSearch = ref('')
const coachSearch = ref('')

// --- Category checkbox logic ---

const isCategoryChecked = (categoryUuid: string): boolean => {
  const total = categoryTotalPlayers(categoryUuid) + categoryTotalCoaches(categoryUuid)
  if (total === 0) return false
  return categorySelectedPlayers(categoryUuid) + categorySelectedCoaches(categoryUuid) === total
}

const isCategoryIndeterminate = (categoryUuid: string): boolean => {
  const total = categoryTotalPlayers(categoryUuid) + categoryTotalCoaches(categoryUuid)
  if (total === 0) return false
  const selected = categorySelectedPlayers(categoryUuid) + categorySelectedCoaches(categoryUuid)
  return selected > 0 && selected < total
}

const categoryTotalPlayers = (categoryUuid: string) =>
  props.players.filter(p => p.categories?.some(c => c.uuid === categoryUuid)).length

const categoryTotalCoaches = (categoryUuid: string) =>
  props.coaches.filter(c => c.categories?.some(c2 => c2.uuid === categoryUuid)).length

const categorySelectedPlayers = (categoryUuid: string) =>
  props.players.filter(p => p.categories?.some(c => c.uuid === categoryUuid) && selectedPlayers.value.includes(p.uuid)).length

const categorySelectedCoaches = (categoryUuid: string) =>
  props.coaches.filter(c => c.categories?.some(c2 => c2.uuid === categoryUuid) && selectedCoaches.value.includes(c.uuid)).length

const toggleCategory = (categoryUuid: string) => {
  const allChecked = isCategoryChecked(categoryUuid)
  const catPlayers = props.players.filter(p => p.categories?.some(c => c.uuid === categoryUuid))
  const catCoaches = props.coaches.filter(c => c.categories?.some(c2 => c2.uuid === categoryUuid))

  if (allChecked) {
    selectedPlayers.value = selectedPlayers.value.filter(uuid => !catPlayers.some(p => p.uuid === uuid))
    selectedCoaches.value = selectedCoaches.value.filter(uuid => !catCoaches.some(c => c.uuid === uuid))
  } else {
    catPlayers.forEach(p => { if (!selectedPlayers.value.includes(p.uuid)) selectedPlayers.value.push(p.uuid) })
    catCoaches.forEach(c => { if (!selectedCoaches.value.includes(c.uuid)) selectedCoaches.value.push(c.uuid) })
  }
}

// Auto-check category when all its members are selected individually
watch([selectedPlayers, selectedCoaches], () => {}, { deep: true })

// --- Player helpers ---

const filteredPlayers = computed(() => {
  const q = playerSearch.value.trim().toLowerCase()
  if (!q) return props.players
  return props.players.filter(p =>
    `${p.first_name} ${p.last_name}`.toLowerCase().includes(q) ||
    `${p.last_name} ${p.first_name}`.toLowerCase().includes(q)
  )
})

const togglePlayer = (uuid: string) => {
  const idx = selectedPlayers.value.indexOf(uuid)
  if (idx === -1) selectedPlayers.value.push(uuid)
  else selectedPlayers.value.splice(idx, 1)
}

// --- Coach helpers ---

const filteredCoaches = computed(() => {
  const q = coachSearch.value.trim().toLowerCase()
  if (!q) return props.coaches
  return props.coaches.filter(c =>
    `${c.first_name} ${c.last_name}`.toLowerCase().includes(q) ||
    `${c.last_name} ${c.first_name}`.toLowerCase().includes(q)
  )
})

const toggleCoach = (uuid: string) => {
  const idx = selectedCoaches.value.indexOf(uuid)
  if (idx === -1) selectedCoaches.value.push(uuid)
  else selectedCoaches.value.splice(idx, 1)
}

// --- Player category labels ---

const playerCategoryNames = (player: Player): string[] =>
  (player.categories || []).map(c => c.name)

const coachCategoryNames = (coach: Coach): string[] =>
  (coach.categories || []).map(c => c.name)
</script>

<template>
  <div>
    <h3 class="text-base font-semibold text-gray-900 mb-1">{{ t('participantSelector.selectParticipants') }}</h3>

    <!-- Categories -->
    <div class="mb-4">
<div class="grid grid-cols-1 min-[500px]:grid-cols-2 min-[700px]:grid-cols-3 min-[1000px]:grid-cols-4 gap-2">
        <button
          v-for="category in categories"
          :key="category.uuid"
          type="button"
          @click="toggleCategory(category.uuid)"
          :class="[
            'flex items-start gap-2 px-3 py-2 text-xs min-[500px]:text-sm border-2 rounded transition-colors text-left w-full',
            isCategoryChecked(category.uuid)
              ? 'border-blue-600 bg-blue-50 text-blue-800'
              : isCategoryIndeterminate(category.uuid)
                ? 'border-blue-400 bg-blue-50 text-blue-700'
                : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400 hover:bg-gray-50'
          ]"
        >
          <span
            :class="[
              'mt-0.5 w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0',
              isCategoryChecked(category.uuid)
                ? 'border-blue-600 bg-blue-600'
                : isCategoryIndeterminate(category.uuid)
                  ? 'border-blue-400 bg-blue-400'
                  : 'border-gray-300 bg-white'
            ]"
          >
            <svg v-if="isCategoryChecked(category.uuid)" class="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
            </svg>
            <span v-else-if="isCategoryIndeterminate(category.uuid)" class="w-2 h-0.5 bg-white rounded-full"></span>
          </span>
          <span class="flex flex-col min-w-0">
            <span class="font-medium truncate">{{ category.name }}</span>
            <span class="text-xs mt-0.5 opacity-75">{{ categorySelectedPlayers(category.uuid) }}/{{ categoryTotalPlayers(category.uuid) }} {{ t('participantSelector.players') }}<template v-if="coaches.length > 0"> · {{ categorySelectedCoaches(category.uuid) }}/{{ categoryTotalCoaches(category.uuid) }} {{ t('participantSelector.coaches') }}</template></span>
          </span>
        </button>
      </div>
    </div>

    <!-- Players Accordion -->
    <div class="border-2 border-gray-300 rounded mb-2">
      <button
        type="button"
        @click="playersAccordionOpen = !playersAccordionOpen"
        class="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <span class="text-sm font-medium text-gray-900">
          {{ t('participantSelector.selectedPlayers') }} ({{ selectedPlayers.length }})
        </span>
        <svg
          :class="['w-4 h-4 text-gray-500 transition-transform', { 'rotate-180': playersAccordionOpen }]"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path fill-rule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clip-rule="evenodd"/>
        </svg>
      </button>

      <div v-show="playersAccordionOpen" class="border-t-2 border-gray-300">
        <!-- Sticky search -->
        <div class="sticky top-0 z-10 bg-white border-b border-gray-200 px-2 pt-2 pb-1">
          <input
            v-model="playerSearch"
            type="text"
            :placeholder="t('participantSelector.searchByLastName')"
            class="w-full px-3 py-1.5 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
          >
        </div>
        <!-- Scrollable list -->
        <div class="max-h-56 overflow-y-auto">
          <div v-if="loadingPlayers" class="flex justify-center py-4">
            <div class="w-5 h-5 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
          </div>
          <div
            v-else
            v-for="player in filteredPlayers"
            :key="player.uuid"
            class="flex items-start gap-2 px-3 py-2 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 cursor-pointer"
            @click="togglePlayer(player.uuid)"
          >
            <input
              type="checkbox"
              :checked="selectedPlayers.includes(player.uuid)"
              @click.stop="togglePlayer(player.uuid)"
              class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 flex-shrink-0 mt-0.5"
            >
            <div class="flex-1 flex flex-col min-[600px]:flex-row min-[600px]:items-center min-[600px]:gap-2">
              <span class="text-sm text-gray-900 flex-1">{{ player.first_name }} {{ player.last_name }}</span>
              <div class="flex gap-1 flex-wrap mt-1 min-[600px]:mt-0 min-[600px]:justify-end">
                <span
                  v-for="catName in playerCategoryNames(player)"
                  :key="catName"
                  class="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded"
                >{{ catName }}</span>
              </div>
            </div>
          </div>
          <div v-if="!loadingPlayers && filteredPlayers.length === 0" class="px-3 py-3 text-sm text-gray-500 text-center">
            {{ t('participantSelector.noResults') }}
          </div>
        </div>
      </div>
    </div>

    <!-- Coaches Accordion -->
    <div v-if="coaches.length > 0" class="border-2 border-gray-300 rounded">
      <button
        type="button"
        @click="coachesAccordionOpen = !coachesAccordionOpen"
        class="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <span class="text-sm font-medium text-gray-900">
          {{ t('participantSelector.selectedCoaches') }} ({{ selectedCoaches.length }})
        </span>
        <svg
          :class="['w-4 h-4 text-gray-500 transition-transform', { 'rotate-180': coachesAccordionOpen }]"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path fill-rule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clip-rule="evenodd"/>
        </svg>
      </button>

      <div v-show="coachesAccordionOpen" class="border-t-2 border-gray-300">
        <!-- Sticky search -->
        <div class="sticky top-0 z-10 bg-white border-b border-gray-200 px-2 pt-2 pb-1">
          <input
            v-model="coachSearch"
            type="text"
            :placeholder="t('participantSelector.searchByLastName')"
            class="w-full px-3 py-1.5 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
          >
        </div>
        <!-- Scrollable list -->
        <div class="max-h-56 overflow-y-auto">
          <div v-if="loadingCoaches" class="flex justify-center py-4">
            <div class="w-5 h-5 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
          </div>
          <div
            v-else
            v-for="coach in filteredCoaches"
            :key="coach.uuid"
            class="flex items-start gap-2 px-3 py-2 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 cursor-pointer"
            @click="toggleCoach(coach.uuid)"
          >
            <input
              type="checkbox"
              :checked="selectedCoaches.includes(coach.uuid)"
              @click.stop="toggleCoach(coach.uuid)"
              class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 flex-shrink-0 mt-0.5"
            >
            <div class="flex-1 flex flex-col min-[600px]:flex-row min-[600px]:items-center min-[600px]:gap-2">
              <span class="text-sm text-gray-900 flex-1">{{ coach.first_name }} {{ coach.last_name }}</span>
              <div class="flex gap-1 flex-wrap mt-1 min-[600px]:mt-0 min-[600px]:justify-end">
                <span
                  v-for="catName in coachCategoryNames(coach)"
                  :key="catName"
                  class="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded"
                >{{ catName }}</span>
              </div>
            </div>
          </div>
          <div v-if="!loadingCoaches && filteredCoaches.length === 0" class="px-3 py-3 text-sm text-gray-500 text-center">
            {{ t('participantSelector.noResults') }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
